<template>
    <BasePanel
        :alvqa="alvqa"
        headline="lang_CONTRACTS"
    >
        <template v-slot:panelContent>
            <div v-if="!hasAnyContractInfo" class="text-center empty-wrapper">
                {{ $t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND') }}
            </div>

            <div
                v-for="(group, groupIdx) in contractsByCompany"
                :key="group.companyName || 'no-company'"
                :class="groupIdx > 0 ? 'mt-5' : 'mt-4'"
            >
                <i18n
                    path="lang_PUBLISHING_WORKS_CAPTION_CONTRACTS_LINKED"
                    tag="div"
                    class="mb-3"
                    :alvqa="alvqa + '-caption-contracts-group-' + groupIdx"
                >
                    <strong place="0">{{ group.companyName }}</strong>
                </i18n>
                <div
                    v-for="contract in group.contracts"
                    :key="contract.alvId"
                    class="mb-4"
                    style="border-left: 3px solid var(--v-logo-base); padding-left: 12px;"
                    :alvqa="alvqa + '-contract-block-' + contract.alvId"
                >
                    <div class="d-flex align-center mb-1">
                        <span class="mr-2" style="width: 160px; flex-shrink: 0;">{{ $t('lang_GENERIC_CONTRACTS_CAPTION_CONTRACT_TYPE') }}:</span>
                        <v-chip small color="chipContractType" text-color="white">{{ contract.contractType }}</v-chip>
                        <span class="ml-2">{{ getContractTypeName(contract.contractType) }}</span>
                    </div>
                    <div class="d-flex align-center mb-1">
                        <span class="mr-2" style="width: 160px; flex-shrink: 0;">{{ $t('lang_GENERIC_CONTRACTS_CONTRACT_TITLE') }}:</span>
                        <span>
                            {{ contract.contractName }}
                            ({{ $t('lang_GENERIC_CAPTION_OPEN_CONTRACT') }}: <a :href="`/contracts/publishing/${contract.alvId}`" target="_blank">{{ contract.alvId }}</a>)
                        </span>
                    </div>
                    <div class="d-flex align-center mb-1">
                        <span class="mr-2" style="width: 160px; flex-shrink: 0;">{{ $t('lang_GENERIC_CONTRACTS_CAPTION_CONTRACTING_PARTY') }}:</span>
                        <span>
                            {{ contract.licensor.name }}
                            <template v-if="$alvPlugin.createUrlForAlvId(contract.licensor.alvId)">
                                ({{ $t('lang_GENERIC_CAPTION_OPEN_AZ') }}: <a :href="$alvPlugin.createUrlForAlvId(contract.licensor.alvId)" target="_blank">{{ contract.licensor.alvId }}</a>)
                            </template>
                            <template v-else>
                                ({{ contract.licensor.alvId }})
                            </template>
                        </span>
                    </div>
                    <div
                        v-if="territoryCodes(contract, 'include').length"
                        class="d-flex align-start mb-1"
                        :alvqa="alvqa + '-contract-territory-include-' + contract.alvId"
                    >
                        <span class="mr-2" style="width: 160px; flex-shrink: 0;">{{ $t('lang_PUBLISHING_WORKS_EXPORT_INCLUDE_TERRITORY') }}:</span>
                        <div>
                            <v-chip
                                v-for="code in territoryCodes(contract, 'include')"
                                :key="'include-' + contract.alvId + '-' + code"
                                small
                                color="iconStandard"
                                text-color="white"
                                class="mr-1 mb-1"
                            >
                                {{ territoryLabel(code) }}
                            </v-chip>
                        </div>
                    </div>
                    <div
                        v-if="territoryCodes(contract, 'exclude').length"
                        class="d-flex align-start mb-1"
                        :alvqa="alvqa + '-contract-territory-exclude-' + contract.alvId"
                    >
                        <span class="mr-2" style="width: 160px; flex-shrink: 0;">{{ $t('lang_PUBLISHING_WORKS_EXPORT_EXCLUDE_TERRITORY') }}:</span>
                        <div>
                            <v-chip
                                v-for="code in territoryCodes(contract, 'exclude')"
                                :key="'exclude-' + contract.alvId + '-' + code"
                                small
                                color="iconStandard"
                                text-color="white"
                                class="mr-1 mb-1"
                            >
                                {{ territoryLabel(code) }}
                            </v-chip>
                        </div>
                    </div>
                </div>
            </div>

            <template v-if="hasSubCatalogues">
                <div class="mt-4 mb-3">
                    {{ $t('lang_PUBLISHING_WORKS_CAPTION_SUBCATALOGUES_LINKED') }}
                </div>
                <div
                    v-for="sub in item.subCatalogues"
                    :key="sub.alvId"
                    class="mb-2"
                    style="border-left: 3px solid var(--v-logo-base); padding-left: 12px;"
                    :alvqa="alvqa + '-subcatalogue-block-' + sub.alvId"
                >
                    <div class="d-flex align-center mb-1">
                        <span class="mr-2" style="width: 160px; flex-shrink: 0;">{{ $t('lang_PUBLISHING_WORKS_CAPTION_SUBCATALOGUE') }}:</span>
                        <span>
                            {{ sub.licensor.name }}
                            ({{ $t('lang_GENERIC_CAPTION_OPEN_CONTRACT') }}: <a :href="`/contracts/publishing/${sub.contractId}`" target="_blank">{{ sub.alvId }}</a>)
                        </span>
                    </div>
                </div>
            </template>

            <div v-if="hasContractHints" class="mt-2">
                <div class="read-only-caption mb-1">{{ $t('lang_PUBLISHING_WORKS_CAPTION_HINTS') }}:</div>
                <div
                    v-for="contract in item.contracts"
                    :key="'hint-' + contract.alvId"
                >
                    <p
                        v-if="contract.notInOriginalShares.missing || contract.notInSocietyShares.missing"
                        class="mb-1"
                    >{{ contract.licensor.name }} <template v-if="$alvPlugin.createUrlForAlvId(contract.licensor.alvId)">(<a :href="$alvPlugin.createUrlForAlvId(contract.licensor.alvId)" target="_blank">{{ contract.licensor.alvId }}</a>)</template><template v-else>({{ contract.licensor.alvId }})</template>{{ $t('lang_GENERIC_MESSAGE_WAS_NOT_FOUND_IN') }}{{ getContractTooltipText(contract) }}</p>
                </div>
            </div>
        </template>
    </BasePanel>
</template>

<script>
import BasePanel from '@/components/_base/BasePanel'

export default {
    name: "WorkContractsPanel",
    components: {
        BasePanel
    },
    props: {
        item: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            alvqa: 'publishing-works-list-row-' + this.item.alvId + '-read-only-panel-contracts'
        }
    },
    computed: {
        contractsByCompany() {
            if (!this.item) return []
            const groups = []
            const seen = {}
            const seenAlvIds = new Set()

            if (this.item.contracts && this.item.contracts.length > 0) {
                for (const contract of this.item.contracts) {
                    const key = contract.connectedCompanyName || ''
                    if (!seen[key]) {
                        seen[key] = { companyName: contract.connectedCompanyName || '', contracts: [] }
                        groups.push(seen[key])
                    }
                    seen[key].contracts.push(contract)
                    if (contract.alvId) seenAlvIds.add(contract.alvId)
                }
            }

            if (this.item.subCatalogues && this.item.subCatalogues.length > 0) {
                for (const sub of this.item.subCatalogues) {
                    const mc = sub.mainContract
                    if (!mc || !mc.alvId || seenAlvIds.has(mc.alvId)) continue
                    const key = mc.connectedCompanyName || ''
                    if (!seen[key]) {
                        seen[key] = { companyName: mc.connectedCompanyName || '', contracts: [] }
                        groups.push(seen[key])
                    }
                    seen[key].contracts.push(mc)
                    seenAlvIds.add(mc.alvId)
                }
            }

            return groups
        },

        hasSubCatalogues() {
            if (!this.item || !this.item.subCatalogues) return false
            return this.item.subCatalogues.length > 0
        },

        hasAnyContractInfo() {
            return this.contractsByCompany.length > 0 || this.hasSubCatalogues
        },

        hasContractHints() {
            if (!this.item || !this.item.contracts) return false
            return this.item.contracts.some(c =>
                (c.notInOriginalShares && c.notInOriginalShares.missing) ||
                (c.notInSocietyShares && c.notInSocietyShares.missing)
            )
        },

        percentages() {
            return [
                {text: this.$t('lang_GENERIC_CAPTION_MANU_PERCENT'), value: "manu"},
                {text: this.$t('lang_GENERIC_CAPTION_ROLE_PERCENT'), value: "role"},
                {text: this.$t('lang_GENERIC_CAPTION_INTL_PERCENT'), value: "intl"},
                {text: this.$t('lang_GENERIC_CAPTION_OWN_PERCENT'), value: "own"},
            ]
        }
    },
    methods: {
        territoryCode(entry) {
            if (entry && typeof entry === 'object') return entry.value || entry.text || ''
            return entry || ''
        },

        territoryCodes(contract, which) {
            const territories = contract && contract.territories
            const list = territories && territories[which] ? territories[which] : []
            return list.map((entry) => this.territoryCode(entry)).filter(Boolean)
        },

        territoryLabel(entry) {
            const code = this.territoryCode(entry)
            const regionGroups = {
                WELT: { de: 'Welt', en: 'World' },
                DACH: { de: 'DACH', en: 'GAS' },
                EUR: { de: 'Europa', en: 'Europe' },
                MEA: { de: 'Naher Osten & Afrika', en: 'Middle East & Africa' },
            }
            if (regionGroups[code]) return regionGroups[code][this.$i18n.locale === 'de' ? 'de' : 'en']
            return this.$alvPlugin.getTranslatedCountryName(code, true) || String(code)
        },

        getContractTypeLabel(contractTypeCode) {
            const types = this.$store.getters.getPublishingContractsTypes
            if (!types || !types.length) return contractTypeCode
            const match = types.find(t => t.value === contractTypeCode)
            if (!match) return contractTypeCode
            return this.$t(match.text)
        },

        getContractTypeName(contractTypeCode) {
            const fullLabel = this.getContractTypeLabel(contractTypeCode)
            return fullLabel.replace(/\s*\([^)]*\)\s*$/, '').trim()
        },

        getContractTooltipText(contract) {
            let societySharesPart = ''
            let originalSharesPart = ''

            if (contract.notInOriginalShares.missing) {
                let missingOriginalSharesText = this.getWorkOriginalShareNames(contract.notInOriginalShares.inShareTypes)
                originalSharesPart = this.$t('lang_PUBLISHING_WORKS_CAPTION_ORIGINAL_SHARES') + ': (' + missingOriginalSharesText.join(', ') + ')'
            }
            if (contract.notInSocietyShares.missing) {
                let missingInSocieties = this.getWorkSocietyNames(contract.notInSocietyShares.inSociety)
                societySharesPart = this.$t('lang_PUBLISHING_WORKS_CAPTION_SOCIETY_SHARES') + ': (' + missingInSocieties.join(', ') + ')'
            }

            return originalSharesPart + ' ' + societySharesPart
        },

        getWorkSocietyNames(societyCodes) {
            let societyNames = []
            if (societyCodes) {
                for (const code of societyCodes) {
                    let societyName = this.$alvPlugin.getSocietyName(parseInt(code))
                    societyNames.push(societyName)
                }
            }
            return societyNames
        },

        getWorkOriginalShareNames(shareTypes) {
            let shareTypeNames = []
            if (shareTypes) {
                for (const code of shareTypes) {
                    let shareTypeName = this.percentages.filter(v => v.value == code)[0].text
                    shareTypeNames.push(shareTypeName)
                }
            }
            return shareTypeNames
        }
    }
}
</script>

<style scoped>
.empty-wrapper {
    color: rgba(0, 0, 0, 0.38);
    font-size: 0.875rem;
    height: 48px;
    line-height: 48px;
}
</style>
